import {
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { Column, ModalBody, Row } from "./customerSettings.styled";
import { Color } from "config/colors";
import { Text } from "components/Typography/text.styled";
import React, { useEffect, useState } from "react";
import { CompanyCustomer, CompanyCustomerProduct } from "model/Company";
import { Api } from "services/api/api.service";
import { useTranslation } from "react-i18next";
import { DatePickerWithLabel } from "../../general/datePickerWithLabel/datePickerWithLabel.component";
import {
  MultiSelectOption,
  MultiSelectWithLabel,
} from "../../general/multiSelectWithLabel/multiSelectWithLabel.component";
import "./customerStyles.css";

type Props = {
  setShowCustomerInvoiceModal: React.Dispatch<React.SetStateAction<boolean>>;
  showCustomerInvoiceModal: boolean;
  customer?: CompanyCustomer;
  companyId?: number;
};

export const CustomerInvoiceModal: React.FC<Props> = ({
  setShowCustomerInvoiceModal,
  showCustomerInvoiceModal,
  customer,
  companyId,
}) => {
  const { t } = useTranslation();

  const [customerProducts, setCustomerProducts] = useState<
    CompanyCustomerProduct[]
  >([]);
  const [selectedCustomerProducts, setSelectedCustomerProducts] = useState<
    any[]
  >([]);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [invoiceRows, setInvoiceRows] = useState<any[]>([]);

  useEffect(() => {
    if (customer?.agreements) {
      let products = customer?.agreements[0].products;
      setCustomerProducts(products);
    }
  }, [customer]);

  const getInvoice = () => {
    setInvoiceRows([]);

    if (startDate && endDate && companyId) {
      let utcStart = Date.UTC(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
      );
      let utcEnd = Date.UTC(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate(),
      );
      Api()
        .company.getInvoiceInformationForPeriod(
          new Date(utcStart),
          new Date(utcEnd),
          selectedCustomerProducts,
          companyId,
        )
        .then((res: any) => {
          let nonEmptyRows = res.data.filter((i: any) => i.totalWorkHours > 0);
          let sorted = nonEmptyRows.sort((a: any, b: any) =>
            a.product.localeCompare(b.product),
          );

          setInvoiceRows(sorted);
        })
        .catch((err) => console.error(err));
    }
  };

  const onModalClose = () => {
    setShowCustomerInvoiceModal(false);
    setInvoiceRows([]);
    setSelectedCustomerProducts([]);
  };

  return (
    <>
      <Modal
        title={customer?.name}
        open={showCustomerInvoiceModal}
        onClose={() => onModalClose()}
      >
        <ModalBody className="w-70-i t-40">
          <Row>
            <Column>
              <Text className="create-invoice">
                {t("CompanyCustomers.CreateInvoice")}
              </Text>
            </Column>
          </Row>

          <Row className="pl-1">
            <Column>
              <Text className="small-title">
                {t("CompanyCustomers.CustomerInformation")}
              </Text>
            </Column>
          </Row>
          <Row className="pl-1">
            <Column className="customer-information">
              {t("CompanyCustomers.Name")}: {customer?.name}
            </Column>
          </Row>
          <Row className="pl-1">
            <Column className="customer-information">
              {t("CompanyCustomers.OrganizationNumber")}:{" "}
              {customer?.organizationNumber}
            </Column>
          </Row>
          <Row className="pl-1">
            <Column className="customer-information">
              {t("CompanyCustomers.Contact")}: {customer?.contact}
            </Column>
          </Row>

          <Row className="pl-1">
            <Column>
              <Text className="small-title">
                {t("CompanyCustomers.ChooseInformationForInvoice")}
              </Text>
            </Column>
          </Row>

          <>
            <Row className="pl-1 flex-i flex-end">
              <Column className="mb-2">
                <DatePickerWithLabel
                  label={t("CreateShift.Date")}
                  required
                  rangeOnly
                  onRangeChange={(dates: Date[]) => {
                    setStartDate(dates[0]);
                    setEndDate(dates[dates.length - 1]);
                  }}
                  placeholder={t("OrganizationalStructure.ChooseDate")}
                  dateFormat="YYYY-MM-DD"
                  allowRangePicker
                />
              </Column>
              <Column className="w-40 mb-2">
                <MultiSelectWithLabel
                  maxWidth={900}
                  label={t("CompanyCustomers.Products")}
                  required
                  options={customerProducts.map(
                    (p) =>
                      ({ label: p.title, value: p.id }) as MultiSelectOption,
                  )}
                  values={selectedCustomerProducts}
                  onChange={(e) => {
                    setSelectedCustomerProducts(e);
                  }}
                  placeholder={t("CompanyCustomers.NotChosen")}
                  maxDisplayTags={3}
                  showSelectAll
                />
              </Column>
              <Column className="invoice-button-container">
                <Text
                  className="invoice-button"
                  onClick={() => getInvoice()}
                  color={Color.BurntSienna}
                >
                  {t("CompanyCustomers.GetInvoiceInformation")}
                </Text>
              </Column>
            </Row>
          </>

          {invoiceRows.length > 0 && (
            <Table className="mb-20 pl-1" size="small" aria-label="purchases">
              <TableHead>
                <TableRow>
                  <TableCell className="regular">
                    {t("CompanyCustomers.Name")}
                  </TableCell>
                  <TableCell className="regular">
                    {t("CompanyCustomers.Product")}
                  </TableCell>
                  <TableCell className="regular">
                    {t("CompanyCustomers.Price")}
                  </TableCell>
                  <TableCell className="regular">
                    {t("CompanyCustomers.TotalHours")}
                  </TableCell>
                  <TableCell className="regular">
                    {t("CompanyCustomers.TotalPrice")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody className="pl-1">
                {invoiceRows.map((invoiceRow: any) => (
                  <TableRow key={invoiceRow.gigId}>
                    <TableCell className="regular" component="th" scope="row">
                      {invoiceRow.workerName}
                    </TableCell>
                    <TableCell className="regular" component="th" scope="row">
                      {invoiceRow.product}
                    </TableCell>
                    <TableCell className="regular">
                      {invoiceRow.hourlyRate}
                    </TableCell>
                    <TableCell className="regular">
                      {invoiceRow.totalWorkHours}
                    </TableCell>
                    <TableCell className="regular">
                      {invoiceRow.totalPrice}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell className="regular fw-600">
                    {t("CompanyCustomers.Total")}
                  </TableCell>
                  <TableCell className="regular fw-600">
                    {invoiceRows.reduce((p, c) => (p += c.totalWorkHours), 0)}
                  </TableCell>
                  <TableCell className="regular fw-600">
                    {invoiceRows.reduce((p, c) => (p += c.totalPrice), 0)} SEK{" "}
                    {t("CompanyCustomers.ExcludingVAT")}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};
